import { cloneDeep } from 'lodash';
import React, { Dispatch, RefObject, SetStateAction, useMemo, useState } from 'react';
import { ContactInput } from 'sr-types/lib/production/v1/graphql';
import { UserProfile } from '../../../common/auth/api';
import { CONSTANTS_ID, EMAIL_TYPE_STRING } from '../../../common/constants/constants';
import { FormProvider } from '../../../common/form/FormContext';
import Validator from '../../../common/form/Validator';
import GuestEditor from '../../../person/GuestEditor';
import { EMPTY_STATE } from '../../../person/person';
import { EMPTY_PERSON_MODAL_STATE } from '../../helpers/productionUtils';

export const getDefaultOwner = (userProfile: UserProfile) => {
    if (!userProfile) return;

    return [
        {
            name: userProfile.name,
            label: userProfile.name.firstName + ' ' + userProfile.name.lastName,
            personReference: userProfile.personReference,
            roles: userProfile.roles
        }
    ];
};

export const doSavePerson = (
    setIsFormSaving: Dispatch<SetStateAction<boolean>>,
    handleChange: (person: ContactInput) => void,
    guestEditorRef: RefObject<any>,
    setPersonModalState: (state: PersonStateType) => void
) => {
    setIsFormSaving(true);
    guestEditorRef.current.handleSave((input: ContactInput, id: string, productionRole: string) => {
        const person: ContactInput = {
            id,
            name: input.name,
            label: `${input.name.firstName} ${input.name.lastName}`,
            contactInfo: input.contactInfo,
            personReference: {
                id,
                label: `${input.name.firstName} ${input.name.lastName}`,
                type: 'Person'
            },
            ...(productionRole ? { productionRoles: [productionRole] } : {})
        };
        handleChange(person);
        setPersonModalState(EMPTY_PERSON_MODAL_STATE);
        setIsFormSaving(false);
    });
};

type PersonStateType = {
    open: boolean;
    prepopulate: {
        name: {
            firstName: string;
            lastName: string;
        };
        email: string;
    };
};

type AddProjectOwnerModalType = {
    personModalState: PersonStateType;
    setIsSaveDisabled: React.Dispatch<React.SetStateAction<boolean>>;
    guestEditorRef: RefObject<HTMLDivElement>;
};

export default function AddProjectOwnerModal({
    personModalState,
    setIsSaveDisabled,
    guestEditorRef
}: AddProjectOwnerModalType) {
    const { prepopulate } = personModalState;

    const [values, setValues] = useState<any>({
        ...cloneDeep(EMPTY_STATE),
        name: prepopulate?.name || { firstName: '', lastName: '' },
        contactInfo: {
            email: prepopulate?.email
                ? { address: prepopulate.email, typeString: EMAIL_TYPE_STRING, verified: false }
                : { address: '', typeString: EMAIL_TYPE_STRING, verified: false },
            phone: ''
        },
        contactExists: false
    });

    const validationRules = useMemo(() => {
        return {
            'name.firstName': [Validator.RULES.isRequired],
            'name.lastName': [Validator.RULES.isRequired],
            'contactInfo.email.address': [Validator.RULES.isRequired, Validator.RULES.email]
        };
    }, []);

    return (
        <FormProvider state={values} setState={setValues} validationRules={validationRules}>
            <GuestEditor
                isInModal
                entityIdFromModal={CONSTANTS_ID.createId}
                setIsSaveDisabled={setIsSaveDisabled}
                ref={guestEditorRef}
                disabledFields={{
                    creativeCredits: { department: true, showReference: true },
                    organizationRole: true,
                    experiences: { deparment: true, title: true, organizationReference: true }
                }}
                hiddenFields={{ creativeCredits: { department: true, showReference: true } }}
            />
        </FormProvider>
    );
}
